const PERCENTILE_COLORS = {
  P3: '#E4E6EF',
  P15: '#D1D5E0',
  P50: '#50CD89',
  P85: '#D1D5E0',
  P97: '#E4E6EF',
};

const PATIENT_COLOR = '#009EF7';

function formatChartValue(value, chart) {
  const decimals = chart.indicator_code === 'IMC' ? 1 : 1;

  return value !== null && value !== undefined
    ? `${Number(value).toFixed(decimals)}${chart.unit ? ` ${chart.unit}` : ''}`
    : '—';
}

export function isPediatricGrowthMode(healthIndicators) {
  return (healthIndicators ?? []).some(
    (indicator) => indicator.health_indicator_chart_type === 'age_percentile'
  );
}

export function buildPediatricChart(chart, referenceStandard) {
  const percentileSeries = (chart.percentile_curves ?? []).map((curve) => ({
    name: curve.label,
    type: 'line',
    data: curve.points.map((point) => [point.age_months, point.value]),
  }));

  const patientSeries = {
    name: 'Paciente',
    type: 'scatter',
    data: (chart.measurements ?? []).map((point) => [point.age_months, point.value]),
  };

  const series = [...percentileSeries, patientSeries];
  const measurements = chart.measurements ?? [];

  const options = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
    },
    colors: [
      ...percentileSeries.map((item) => PERCENTILE_COLORS[item.name] ?? '#E4E6EF'),
      PATIENT_COLOR,
    ],
    stroke: {
      curve: 'straight',
      width: [...percentileSeries.map((item) => (item.name === 'P50' ? 2.5 : 1.5)), 0],
      dashArray: [...percentileSeries.map((item) => (item.name === 'P50' ? 0 : 4)), 0],
    },
    markers: {
      size: [...percentileSeries.map(() => 0), 6],
      strokeWidth: [...percentileSeries.map(() => 0), 2],
      hover: { size: 8 },
    },
    dataLabels: { enabled: false },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '11px',
    },
    grid: {
      borderColor: '#eff2f5',
      strokeDashArray: 4,
    },
    xaxis: {
      type: 'numeric',
      title: { text: 'Idade (meses)', style: { fontSize: '11px', color: '#7e8299' } },
      labels: {
        style: { fontSize: '11px', colors: '#7e8299' },
        formatter: (value) => `${Math.round(value)}`,
      },
      min: 0,
    },
    yaxis: {
      title: {
        text: chart.unit ? `${chart.indicator_name} (${chart.unit})` : chart.indicator_name,
        style: { fontSize: '11px', color: '#7e8299' },
      },
      labels: {
        style: { fontSize: '11px', colors: '#7e8299' },
        formatter: (value) => {
          if (value === null || value === undefined) {
            return '';
          }

          const decimals = chart.indicator_code === 'IMC' ? 1 : 1;

          return Number(value).toFixed(decimals);
        },
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom({ series, seriesIndex, dataPointIndex }) {
        if (seriesIndex !== series.length - 1) {
          return '';
        }

        const measurement = measurements[dataPointIndex];

        if (!measurement) {
          return '';
        }

        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">Paciente</div>
          <div>Idade: ${measurement.age_months} meses</div>
          <div>Valor: ${formatChartValue(measurement.value, chart)}</div>
          <div>Percentil: P${measurement.percentile}</div>
          <div>Z-score: ${measurement.z_score}</div>
        </div>`;
      },
    },
    subtitle: {
      text: referenceStandard ?? '',
      align: 'left',
      style: { fontSize: '10px', color: '#A1A5B7' },
    },
  };

  return { options, series };
}

export function buildPediatricSummaryCards(charts) {
  return (charts ?? []).map((chart) => {
    const latest = [...(chart.measurements ?? [])].sort((a, b) => b.age_months - a.age_months)[0];

    if (!latest) {
      return null;
    }

    let status = 'neutral';

    if (latest.percentile >= 3 && latest.percentile <= 97) {
      status = 'normal';
    } else if (latest.percentile < 3) {
      status = 'low';
    } else {
      status = 'high';
    }

    return {
      id: chart.indicator_id,
      code: chart.indicator_code,
      name: chart.indicator_name,
      unit: chart.unit,
      latestValue: latest.value,
      percentile: latest.percentile,
      zScore: latest.z_score,
      ageMonths: latest.age_months,
      status,
    };
  }).filter(Boolean);
}

export function formatPercentile(percentile) {
  if (percentile === null || percentile === undefined) {
    return '—';
  }

  return `P${percentile}`;
}
